import { api } from "./api";

// When a logged-out visitor clicks "Enroll now" or tries to redeem a
// voucher on the homepage, they get sent to /login first. Without this,
// that intent was silently dropped - they'd land on the dashboard after
// logging in with no idea they still needed to go back and re-enroll.
// This stashes what they were trying to do (in sessionStorage, so it
// survives the login/register page load but not much longer) and
// LoginPage/RegisterPage replay it automatically right after auth succeeds.
const KEY = "ilms_pending_action";

export function setPendingAction(action) {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(action));
  } catch {
    // sessionStorage unavailable (e.g. private browsing edge cases) - the
    // user just falls back to manually re-enrolling after login, same as
    // before this existed.
  }
}

// Read without consuming - lets the login/register page show a one-line
// hint ("log in to finish enrolling") without eating the action before the
// form is actually submitted.
export function peekPendingAction() {
  try {
    const raw = sessionStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function takePendingAction() {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    sessionStorage.removeItem(KEY);
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

// Call this right after a successful login/register. Returns true if it
// took over navigation itself (the caller should not also navigate anywhere
// else), false if there was nothing pending (caller should do its normal
// post-auth redirect).
export async function resumePendingAction(navigate) {
  const action = takePendingAction();
  if (!action) return false;

  try {
    if (action.type === "voucher") {
      const result = await api.post(`/api/courses/${action.courseSlug}/redeem-voucher`, { code: action.code });
      navigate(`/learn/${result.courseSlug}`);
      return true;
    }
    if (action.type === "checkout") {
      const result = await api.post(`/api/checkout/${action.courseId}`, {});
      if (result.alreadyEnrolled) {
        navigate(`/learn/${action.courseSlug}`);
        return true;
      }
      if (result.testMode) {
        navigate(result.url);
      } else {
        window.location.href = result.url;
      }
      return true;
    }
  } catch (err) {
    // Couldn't finish it automatically (wrong voucher code, course filled up
    // while they were logging in, etc.) - send them home with the problem
    // explained instead of just silently failing.
    navigate(`/?pendingActionError=${encodeURIComponent(err.message)}`);
    return true;
  }
  return false;
}
